'use client';

/**
 * Injects a CSS string into document.head once (idempotent): creates a <style> with `styleId` on first mount; later
 * calls with the same id are no-ops and the element persists for the page lifetime.
 *
 * EVERY INJECTION ALSO ARMS THE REDUCED-MOTION POLICY (DL-1.10, 2026-08-20). Runtime style injection is what a surface
 * does when it renders OUTSIDE the sheet that would otherwise carry its rules -- an MFE mounted into a host, a viewer
 * embedded in a shell -- which is exactly the surface `REDUCED_MOTION.CSS` cannot reach through the emitted base.css.
 * Arming it here rather than at each call site means a newly-injected stylesheet inherits the guard by construction,
 * and the guard is idempotent under its own id, so N injections still yield ONE element.
 */

import * as React from 'react';
import { REDUCED_MOTION } from '@federa/const/reduced-motion';

/** Append a `<style id>` carrying `css` unless an element already holds that id. */
function injectOnce(styleId: string, css: string): void {
	if (document.getElementById(styleId)) return;
	const style = document.createElement('style');
	style.id = styleId;
	style.textContent = css;
	document.head.appendChild(style);
}

export function useInjectStyles(styleId: string, css: string): void {
	React.useEffect(() => {
		injectOnce(REDUCED_MOTION.STYLE_ID, REDUCED_MOTION.CSS);
		injectOnce(styleId, css);
	}, [styleId, css]);
}
